import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * Native-only Android hardware back button / gesture handling (no-op on web
 * and on iOS, which has no back button — Capacitor still fires `backButton`
 * only on Android).
 *
 * Without this, the WebView's own default handling applies, which is
 * inconsistent across Android versions/devices — anything from "does
 * nothing" to closing the app without warning from a screen that isn't the
 * app's root.
 *
 * The fix routes the hardware back button through `window.history.back()` —
 * the SAME mechanism a browser's own back button already uses on web. This
 * is deliberate, not just convenient: App.tsx's data router (createBrowserRouter)
 * already treats "browser back" as one of the navigation surfaces
 * useLeaveGameGuard's blocker covers (see that file's comment — "leaving
 * /game/:id goes through ONE decision for every navigation surface...
 * browser back, programmatic nav"). Reusing history.back() means the
 * Android back button automatically gets the SAME "are you sure you want to
 * leave this game?" confirmation as every other way of navigating away,
 * with no separate wiring.
 *
 * When there is nowhere left to go back to (`canGoBack` false — the WebView's
 * own back-forward list is empty, e.g. the user is on the app's landing
 * screen), the standard Capacitor pattern applies: exit the app rather than
 * leaving the back button appearing to do nothing.
 */
export function initNativeBackButton(): void {
  if (!Capacitor.isNativePlatform()) return;

  void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      void CapacitorApp.exitApp();
    }
  });
}
